import { Carousel } from 'react-bootstrap'
import styled from 'styled-components'

/**
 * Kiosk display design system – responsive from small tablets to Full HD (1920×1080) TVs.
 * High contrast, fluid typography, clear presence/absence and token visibility.
 */

// Palette: deep slate + warm gold/amber accents, white for tokens
const colors = {
  bg: '#0f1419',
  surface: '#1a2332',
  surfaceLight: '#243044',
  accent: '#e8b84a',
  accentDim: '#c49b3d',
  primary: '#5b7cfa',
  primaryBright: '#7b9aff',
  text: '#f0f2f5',
  textMuted: '#94a3b8',
  success: '#22c55e',
  successGlow: 'rgba(34, 197, 94, 0.4)',
  absent: '#ef4444',
  absentGlow: 'rgba(239, 68, 68, 0.35)',
  border: 'rgba(255, 255, 255, 0.08)',
}

const breakpoints = {
  sm: '480px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  fullHd: '1920px',
}

export const ClinicCounterBackground = styled.div`
  /* Kiosks are unattended displays; nothing must scroll off screen.
   * height: 100% of the viewport, overflow hidden so the token footer
   * (rendered last) is always pinned to the bottom even when the body
   * content (carousel + cards) would otherwise overflow. */
  height: 100vh;
  width: 100%;
  min-height: 320px;
  max-width: 100vw;
  background: ${colors.bg};
  display: flex;
  flex-direction: column;
  font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
  overflow: hidden;

  @media (min-width: ${breakpoints.fullHd}) {
    min-height: 1080px;
  }
`

export const ClinicCounterHeader = styled.div`
  background: linear-gradient(135deg, ${colors.surface} 0%, ${colors.surfaceLight} 100%);
  width: 100%;
  font-size: clamp(16px, 2vw, 32px);
  font-weight: 600;
  color: ${colors.text};
  letter-spacing: 0.02em;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  height: clamp(56px, 8vh, 72px);
  min-height: 56px;
  position: relative;
  border-bottom: 1px solid ${colors.border};
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
`

export const ClinicCounterFooter = styled.div`
  /* The token strip is the primary purpose of the screen. Floor it at a
   * generous height (≥ 200px) so token numbers stay legible even on short
   * windows, and never let it shrink (flex-shrink: 0). */
  min-height: clamp(200px, 26vh, 320px);
  height: clamp(200px, 26vh, 320px);
  width: 100%;
  display: grid;
  grid-template-columns: ${({ hasQrSlot }) =>
    hasQrSlot ? '0.32fr 0.32fr 0.36fr' : '0.55fr 0.45fr'};
  gap: 0;
  border-top: 1px solid ${colors.border};
  background: ${colors.surface};
  box-shadow: 0 -12px 36px rgba(0, 0, 0, 0.35);
  flex-shrink: 0;

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: ${({ hasQrSlot }) => (hasQrSlot ? '1fr 1fr' : '1fr 1fr')};
    grid-template-rows: ${({ hasQrSlot }) => (hasQrSlot ? 'auto auto' : 'auto')};
    height: auto;
    min-height: 220px;
  }
`

export const ClinicCounterAppointmentNumberBox = styled.div`
  background: ${(props) =>
    props?.main
      ? `linear-gradient(145deg, ${colors.primary} 0%, #4a63d4 100%)`
      : `linear-gradient(145deg, ${colors.surfaceLight} 0%, ${colors.surface} 100%)`};
  color: ${(props) => (props?.main ? '#fff' : 'rgba(255, 255, 255, 0.85)')};
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: clamp(10px, 1.4vw, 20px) clamp(16px, 2.5vw, 36px);
  overflow: hidden;
  min-height: 0;
  border-right: ${(props) => (props?.main ? 'none' : `1px solid ${colors.border}`)};
  box-shadow: ${(props) =>
    props?.main
      ? 'inset 0 1px 0 rgba(255,255,255,0.18), 0 6px 26px rgba(91, 124, 250, 0.28)'
      : 'inset 0 1px 0 rgba(255,255,255,0.04)'};
  min-width: 0;

  @media (max-width: ${breakpoints.sm}) {
    border-right: ${(props) => (props?.main ? 'none' : `1px solid ${colors.border}`)};
    border-bottom: 1px solid ${colors.border};
  }
`

export const ClinicCounterAppointmentNumberText = styled.div`
  font-size: ${(props) => (props?.big ? 'clamp(72px, 8vw, 120px)' : 'clamp(20px, 1.8vw, 28px)')};
  font-weight: ${(props) => (props?.big ? '800' : '500')};
  letter-spacing: ${(props) => (props?.big ? '-0.02em' : '0')};
  line-height: 1.1;
  text-shadow: ${(props) =>
    props?.big ? '0 2px 20px rgba(0,0,0,0.2), 0 0 40px rgba(255,255,255,0.08)' : 'none'};
  ${(props) =>
    props?.second &&
    `
    font-size: ${props?.big ? 'clamp(56px, 5.5vw, 88px)' : 'clamp(18px, 1.4vw, 24px)'};
  `};
`

export const ClinicCounterBody = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  /* When gridTemplate is passed, it wins (used for the no-carousel layouts:
   * '1fr', '1fr 1fr', etc). Otherwise we fall through to the standard
   * carousel-present 2-/3-column templates. */
  grid-template-columns: ${({ gridTemplate, singleMentor }) =>
    gridTemplate
      ? gridTemplate
      : singleMentor
        ? 'minmax(min(220px, 26vw), 0.22fr) 1fr'
        : 'minmax(min(200px, 22vw), 0.2fr) 1fr minmax(min(220px, 22vw), 0.22fr)'};
  gap: 0;

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: ${({ gridTemplate, singleMentor }) =>
      gridTemplate
        ? gridTemplate
        : singleMentor
          ? 'minmax(96px, 0.24fr) 1fr'
          : 'minmax(88px, 0.2fr) 1fr minmax(88px, 0.2fr)'};
  }
`

export const ClinicCounterBodyDoctorDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(10px, 1.4vw, 18px);
  align-items: center;
  justify-content: flex-start;
  padding: clamp(16px, 2vw, 28px) clamp(10px, 1.5vw, 20px);
  border-right: ${(props) => (props.$isRightColumn ? 'none' : `1px solid ${colors.border}`)};
  border-left: ${(props) => (props.$isRightColumn ? `1px solid ${colors.border}` : 'none')};
  background: linear-gradient(180deg, ${colors.surface} 0%, ${colors.bg} 100%);
  overflow: hidden;
  min-width: 0;

  @media (max-width: ${breakpoints.sm}) {
    padding: 10px;
    gap: 10px;
  }
`

export const ClinicCounterBodyDoctorImage = styled.img`
  width: 100%;
  max-width: 160px;
  aspect-ratio: 1;
  border-radius: 16px;
  object-fit: cover;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
`

export const StyledCarouselClinicCounter = styled(Carousel)`
  overflow: hidden;
  .carousel-control-prev {
    display: none;
  }
  .carousel-control-next {
    display: none;
  }
  .carousel-indicators {
    display: none;
  }
  .carousel-inner {
    height: 100%;
  }
  .carousel-item {
    height: 100%;
  }
`

export const ClinicCarouselSlide = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  background: #000;
`

/**
 * Display message (OOH) carousel image.
 * Optimal source image size: 1920×1080 px (16:9) for full HD kiosks.
 * Min recommended: 1280×720 px. Images use object-fit: contain and scale to the center panel.
 */
export const ClinicCarouselSlideImage = styled.img`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  object-fit: contain;
  ${(props) => props?.blackTint && `filter: brightness(0.5)`};
`

export const ClinicCarouselTextContainer = styled.div`
  color: #fff;
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 24px;
  gap: 12px;
`

export const ClinicCarouselText = styled.div`
  font-size: ${(props) => (props?.main ? 'clamp(18px, 1.8vw, 28px)' : 'clamp(14px, 1.2vw, 20px)')};
  font-weight: ${(props) => (props?.main ? '700' : '400')};
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
`

export const ScreeningMentorAppointmentBox = styled.div`
  ${({ primary }) => `
    background: ${primary ? `linear-gradient(145deg, ${colors.primary} 0%, #4a63d4 100%)` : colors.surfaceLight};
    color: ${primary ? '#fff' : colors.textMuted};
    padding: clamp(6px, 1vw, 12px) clamp(8px, 1vw, 12px);
    border-radius: 12px;
    min-height: 0;
    width: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: center;
    border: 1px solid ${primary ? 'rgba(255,255,255,0.15)' : colors.border};
    box-shadow: ${primary ? '0 4px 16px rgba(91, 124, 250, 0.2)' : 'none'};
  `}
`

// New styled components for mentor card presence and token emphasis
export const MentorAvatarWrap = styled.div`
  position: relative;
  width: 100%;
  max-width: min(200px, 92%);
  aspect-ratio: 1;
  flex-shrink: 0;

  @media (max-width: ${breakpoints.md}) {
    max-width: min(150px, 90%);
  }

  @media (max-width: ${breakpoints.sm}) {
    max-width: 96px;
  }
`

export const MentorAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 24px;
  object-fit: cover;
  box-shadow:
    0 16px 40px rgba(0, 0, 0, 0.45),
    0 0 0 1px rgba(255, 255, 255, 0.06) inset;
  border: 2px solid rgba(255, 255, 255, 0.1);
`

export const PresenceBadge = styled.div`
  /* Sibling-below-avatar chip — never overlay the practitioner's face. */
  display: inline-flex;
  align-items: center;
  padding: clamp(4px, 0.7vw, 7px) clamp(10px, 1.3vw, 18px);
  border-radius: 999px;
  font-size: clamp(11px, 1vw, 15px);
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #fff;
  background: ${(props) => (props?.present ? colors.success : colors.absent)};
  box-shadow: 0 4px 14px
    ${(props) => (props?.present ? colors.successGlow : colors.absentGlow)};
  border: 1px solid rgba(255, 255, 255, 0.18);
  white-space: nowrap;
  align-self: center;
`

export const TokenLabel = styled.span`
  display: block;
  font-size: clamp(13px, 1.25vw, 22px);
  font-weight: 600;
  opacity: 0.92;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`

export const TokenValue = styled.span`
  display: block;
  /* Height-aware so the giant "now serving" number never crowds out the
   * label + patient name within the fixed-height footer box (which would
   * push the name off the bottom of the screen). The min() ceiling keeps it
   * proportional to the footer's own height. */
  font-size: min(clamp(48px, 7vw, 120px), 17vh);
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: 1.02;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 2px 28px rgba(0, 0, 0, 0.28);
  @keyframes tokenPulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.93; transform: scale(1.018); }
  }
  &.current-token {
    animation: tokenPulse 2.4s ease-in-out infinite;
  }

  @media (max-width: ${breakpoints.sm}) {
    font-size: clamp(40px, 12vw, 64px);
  }
`

export const TokenPatientName = styled.span`
  display: block;
  font-size: clamp(12px, 1.05vw, 18px);
  font-weight: 500;
  opacity: 0.85;
  margin-top: 4px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const MentorAvatarPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 24px;
  background: linear-gradient(145deg, #243044 0%, #1a2332 100%);
  border: 2px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(28px, 4.5vw, 64px);
  font-weight: 700;
  color: rgba(255, 255, 255, 0.55);
  letter-spacing: 0.02em;
  box-shadow:
    0 16px 40px rgba(0, 0, 0, 0.45),
    0 0 0 1px rgba(255, 255, 255, 0.04) inset;
`
